import { BehaviorSubject } from 'rxjs';
import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { ChooseState, ChooseStateValidator } from '../create-block/create-block.model';
import { UserPermissions } from 'ngx-core-services';

export const getLoggerState = <T extends ChooseState['name']>(
  name: T,
  id: number,
  img: string,
  _this: ChooseState[],
  mutuallyExclusive = true,
  disabled = false,
  mutuallyExclusiveWith: string[] = []
): ChooseState => {
  const checked$ = new BehaviorSubject<boolean>(false);
  return {
    name,
    id,
    img,
    checked$,
    disabled,
    changeChecked: () => {
      checked$.next(!checked$.value);
      _this.forEach((logger) => {
        if (logger.name !== name && mutuallyExclusive) {
          logger.checked$.next(false);
        }

        if (mutuallyExclusiveWith.length > 0 && mutuallyExclusiveWith.includes(logger.name)) {
          logger.checked$.next(false);
        }
      });
    },
  };
};

export const loggerFormValidator = (opts = { mutuallyExclusive: true }): ChooseStateValidator => ((control: AbstractControl<ChooseState[]>) => {
  const checked = control.value.some((logger) => logger.checked$.value);

  const isVirtualMonitoringPreferenceSelected = isStickLoggerSelected(control.value);

  if (isVirtualMonitoringPreferenceSelected && opts.mutuallyExclusive) {
    control.parent?.get('connextSerial')?.setValue('');
  }

  if (!checked) {
    return { loggerRequired: true };
  }
  return null;
});

export const connextSerialValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const formGroup = control as FormGroup;
  const monitoringPreference = formGroup.get('monitoringPreference')?.value;
  const connextSerial = formGroup.get('connextSerial')?.value;

  const isConnextMonitoringPreferenceSelected = isConnextLoggerSelected(monitoringPreference);
  if (isConnextMonitoringPreferenceSelected && !connextSerial) {
    return { connextSerialRequired: true };
  }

  return null;
};

export const azzurroHubSerialValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const formGroup = control as FormGroup;
  const monitoringPreference = formGroup.get('monitoringPreference')?.value;
  const azzurroHubSerial = formGroup.get('azzurroHubSerial')?.value;

  const isAzzurroHubMonitoringPreferenceSelected = isAzzurroHubLoggerSelected(monitoringPreference);

  if (isAzzurroHubMonitoringPreferenceSelected && !azzurroHubSerial) {
    return { azzurroHubSerialRequired: true };
  }

  return null;
};

export const inverterListValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const formGroup = control as FormGroup;
  const inverterList = formGroup.get('inverterList')?.value;
  if (inverterList && inverterList.length === 0) {
    return { inverterListRequired: true };
  }

  return null;
};

export const plantUniqueEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const formGroup = control as FormGroup;
  const newEmail = control.value;
  const plantEmails = formGroup.parent?.parent

  if (plantEmails instanceof FormArray && newEmail) {
    const isUnique = plantEmails.controls.every((user: AbstractControl) => user.value.email !== newEmail);
    return isUnique ? null : { uniqueEmail: true };
  }

  return null;
}

export const serialUniqueValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const formGroup = control as FormGroup;
  const newSerial = control.value;
  const plantDeviceSerials = formGroup.parent?.parent

  if (plantDeviceSerials instanceof FormArray && newSerial) {
    const isUnique = plantDeviceSerials.controls.every((user: AbstractControl) => user.value.inverterSerial !== newSerial);
    return isUnique ? null : { uniqueSerial: true };
  }

  return null;
}

export const hasPlantOwner = (control: AbstractControl): ValidationErrors | null => {
  const formGroup = control as FormGroup;
  const users = formGroup.controls.users.value;
  const isValid = users.some((user: any) => user.userLevel === UserPermissions.Owner);

  return isValid ? null : { hasPlantOwner: true };
}

/** Validator
 * Power should be a number greater than 1000 (W)
 */
export const POWER_VALIDATOR = Validators.pattern('^[1-9][0-9]{3,}$');

export const isConnextLoggerSelected = (monitoringPreference?: ChooseState[] | null): boolean =>
    !!monitoringPreference?.some((logger) => logger.name === 'connext' && logger.checked$.value);

export const isAzzurroHubLoggerSelected = (monitoringPreference?: ChooseState[] | null): boolean =>
    !!monitoringPreference?.some((logger) => logger.name === 'azzurroHub' && logger.checked$.value);

export const isStickLoggerSelected = (monitoringPreference?: ChooseState[] | null): boolean =>
    !!monitoringPreference?.some((logger) => logger.name === 'others' && logger.checked$.value);

export const getInverterSerialErrorMessage = (labels: any, error?: ValidationErrors | null) => {
  if (error?.uniqueSerial) {
    return labels['uniqueSerial'];
  }
  if (error?.serialNotValid) {
    return labels['serialNotValid'];
  }
  if (error?.deviceNotExist) {
    return labels['deviceNotExists'];
  }
  if (error?.deviceAlreadyOnACertifiedPlant) {
    return labels['deviceAlreadyOnACertifiedPlant'];
  }
  return labels['validationError'];
};

export const getUserEmailErrorMessage = (labels: any, error?: ValidationErrors | null) => {
  if (error?.email) {
    return labels['emailNotValid'];
  }
  if (error?.uniqueEmail) {
    return labels['uniqueEmail'];
  }
  return labels['validationError'];
}

export const getConnextErrorMessage = (labels: any, error?: ValidationErrors | null) => {
  if (error?.serialNotValid) {
    return labels['serialNotValid'];
  }
  if (error?.deviceNotExist) {
    return labels['deviceNotExists'];
  }
  if (error?.unique) {
    return labels['deviceAlreadyExistsInYourSystemList'];
  }
  if (error?.hasOwnerAndItsNotMe) {
    return labels['hasOwnerAndItsNotYou'];
  }
  if (error?.iamAlreadyAdmin) {
    return labels['iAmAlreadyAdmin'];
  }
  if (error?.deviceNotOnline) {
    return labels['deviceNotOnline'];
  }
  if (error?.fwVersionNotValid) {
    return labels['fwVersionNotValidUpgradeRequired'];
  }
  if (error?.azzurroHubBusy) {
    return labels['azzurroHubBusy'];
  }
  if (error?.fwUpdating) {
    return labels['fwUpgradeInProgress'];
  }
  if (error?.deviceAlreadyOnACertifiedPlant) {
    return labels['deviceAlreadyOnACertifiedPlant'];
  }
  return labels['validationError'];
};
